import { useEffect } from 'react'

import { faUser, faBuilding } from '@fortawesome/pro-light-svg-icons'

import { useFilterString, useSetState } from '@domain-group/react-hooks'
import {
  FormField, InputSearch, LoadingModule, SegmentedControl, SidebarModal,
} from '@domain-group/suit-theme'

import useUserCampaignQuoteTemplates from '@hooks/useUserCampaignQuoteTemplates'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import type { CampaignQuotePayload } from '@hooks/useCampaignQuote'
import type { CurrentUserPayload } from '@hooks/useCurrentUser'

import CampaignQuoteTemplate from './components/CampaignQuoteTemplate'
import NoQuotes from './components/NoQuotes'

interface CreateFromTemplateProps {
  callbacks: {
    afterAction: VoidFunction,
    closeModal: () => void,
    createQuoteFromTemplate: (params: HandleCallbackActionParams) => void,
  },
  campaignQuotePayload: CampaignQuotePayload,
  currentUserPayload: CurrentUserPayload,
  requestOptions?: object,
}

const defaultState = {
  filterKey: 'all',
}

const CreateFromTemplate = (props: CreateFromTemplateProps) => {
  const {
    callbacks: {
      afterAction,
      createQuoteFromTemplate,
    },
    campaignQuotePayload: {
      callbacks: {
        createCampaignQuoteFromTemplate: createFn,
      },
      campaignQuote,
    },
    currentUserPayload: {
      isAgencyAdmin,
    },
    requestOptions,
  } = props

  const [state, setState] = useSetState(defaultState)
  const { filterKey } = state

  const { onChange, value } = useFilterString()

  const userCampaignQuoteTemplatesPayload = useUserCampaignQuoteTemplates({
    filters: {
      builder_version: 2,
      string: value,
    },
    performHttpRequests: true,
  })

  const {
    callbacks: {
      getFilteredUserTemplates,
    },
    currentUserQuoteTemplates,
    loading,
  } = userCampaignQuoteTemplatesPayload

  const filteredTemplates = getFilteredUserTemplates(
    filterKey,
    isAgencyAdmin,
  )

  useEffect(() => {
    if (!!currentUserQuoteTemplates.length && !isAgencyAdmin){
      setState({ filterKey: 'user' })
    }
  }, [currentUserQuoteTemplates.length, isAgencyAdmin])

  return (
    <SidebarModal.Content hasFooter={false}>

      {!!currentUserQuoteTemplates.length && !isAgencyAdmin && (
        <SegmentedControl
          activeButtonKey={filterKey}
          boxProps={{ marginBottom: 'large' }}
          buttons={[
            // @ts-expect-error TS2322
            { key: 'user', icon: faUser, label: 'My Templates' },
            // @ts-expect-error TS2322
            { key: 'agency', icon: faBuilding, label: 'Agency Templates' },
          ]}
          onClick={key => setState({ filterKey: key })}
        />
      )}

      <FormField label="Search Templates">
        {/*
         // @ts-expect-error TS2741 */}
        <InputSearch
          onChange={onChange}
          value={value}
        />
      </FormField>

      <FormField label="Quote Templates" marginTop="large">
        <LoadingModule loading={loading} times={3} />

        {!loading && !filteredTemplates.length && <NoQuotes />}

        {filteredTemplates.map(campaignQuoteTemplate => (
          <CampaignQuoteTemplate
            callbacks={{
              selectCampaignQuoteTemplate: () => createQuoteFromTemplate({
                callbacks: {
                  // @ts-expect-error TS2322
                  action: createFn,
                  afterAction,
                  // @ts-expect-error TS2322
                  buildActionParams: () => [
                    campaignQuote,
                    campaignQuoteTemplate.id,
                    requestOptions,
                  ],
                },
                toastText: 'Campaign Quote Created',
              }),
            }}
            campaignQuoteTemplate={campaignQuoteTemplate}
            key={campaignQuoteTemplate.id}
          />
        ))}
      </FormField>
    </SidebarModal.Content>
  )
}

export default CreateFromTemplate
